import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useDispatch } from '@tmapy/redux';
import type { CallApiResult } from '@tmapy/beetlejuice-utils';

import { QUERY_STATE } from '../constants';
import { actionResourceInvalidate } from '../redux/resource/actions';

export type ActionQueryStateSetter = Dispatch<SetStateAction<QUERY_STATE>>;

export type ActionCallArgs<Q> = { query: Q; request: any };

/** `null` znamená, že akce byla přerušena ještě před odesláním (např. neprošla validace). */
export type Action<Q, R = any> = (args: ActionCallArgs<Q>) => Promise<CallApiResult<R> | null>;

export type UseActionProps<Q> = {
  query: Q;
  onRefetch?: () => unknown;
  routeList?: () => void;
  listPath?: string;
  onStart?: () => void;
  onFail?: (response: CallApiResult<any>) => void;
};

export type RunActionOptions<Q> = {
  refetch?: boolean;
  redirectToList?: boolean;
  query?: Q;
  request?: object | null;
};

export type UseAction<Q> = {
  run: <R = any>(
    action: Action<Q, R>,
    changeState: ActionQueryStateSetter,
    options?: RunActionOptions<Q>,
  ) => Promise<CallApiResult<R> | undefined>;
  isActionRunning: boolean;
};

export const toBtnQueryState = (state: QUERY_STATE) =>
  state === QUERY_STATE.WAITING ? 'waiting' : undefined;

/**
 * Spouští akce nad záznamem (uzavřít, odeslat, smazat…) a drží zámek po celou dobu běhu
 * včetně refetche, aby opakované kliknutí neodeslalo stejný požadavek podruhé.
 */
export const useAction = <Q>({
  query: defaultQuery,
  onRefetch,
  routeList,
  listPath,
  onStart,
  onFail,
}: UseActionProps<Q>): UseAction<Q> => {
  const dispatch = useDispatch();
  const runningRef = useRef(false);
  const [isActionRunning, setIsActionRunning] = useState(false);

  const run = useCallback(
    async <R = any>(
      action: Action<Q, R>,
      changeState: ActionQueryStateSetter,
      { refetch = true, redirectToList = false, query, request }: RunActionOptions<Q> = {},
    ): Promise<CallApiResult<R> | undefined> => {
      if (runningRef.current) return undefined;
      runningRef.current = true;
      setIsActionRunning(true);

      let response: CallApiResult<R> | null | undefined;
      try {
        changeState(QUERY_STATE.WAITING);
        onStart?.();
        response = await action({ query: query ?? defaultQuery, request });
        if (response === null) {
          changeState(QUERY_STATE.EMPTY);
        } else if (response.ok) {
          changeState(QUERY_STATE.SUCCESS);
          if (redirectToList) routeList?.();
        } else {
          changeState(QUERY_STATE.FAIL);
          onFail?.(response);
        }
      } catch {
        changeState(QUERY_STATE.FAIL);
      } finally {
        if (redirectToList && listPath) {
          dispatch(actionResourceInvalidate(listPath));
        }
      }

      if (refetch && response !== null) {
        try {
          await onRefetch?.();
        } catch {
          // Chyba refetche se projeví ve stavu resource, zámek se musí uvolnit i tak.
        }
      }

      runningRef.current = false;
      setIsActionRunning(false);
      return response ?? undefined;
    },
    [defaultQuery, dispatch, listPath, onFail, onRefetch, onStart, routeList],
  );

  return { run, isActionRunning };
};
